import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Gauge, Search, FileText, Download, CheckCircle,
  AlertTriangle, Clock, Loader2, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, Calendar, MapPin
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  atentie: { label: 'Atentie', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  expira_curand: { label: 'Expira curand', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  expirat: { label: 'Expirat', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  necunoscut: { label: 'Necunoscut', color: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

const AuditEMMPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(0);
  const observerRef = useRef(null);

  const fileProp = React.useMemo(() => {
    if (!pdfData) return null;
    return { data: new Uint8Array(pdfData) };
  }, [pdfData]);

  const containerRefCallback = useCallback((node) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (node) {
      const w = node.clientWidth - 8;
      if (w > 0) setContainerWidth(w);
      const ro = new ResizeObserver(() => {
        const newW = node.clientWidth - 8;
        if (newW > 0) setContainerWidth(newW);
      });
      ro.observe(node);
      observerRef.current = ro;
    }
  }, []);

  useEffect(() => { return () => { if (observerRef.current) observerRef.current.disconnect(); }; }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, settingsRes] = await Promise.all([
        api.get('/emm/equipment'),
        api.get('/emm/settings')
      ]);
      setItems(itemsRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      toast.error('Eroare la incarcarea echipamentelor EMM');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGeneratePdf = async () => {
    try {
      setPdfLoading(true);
      const signerName = settings?.responsabil_nume || '';
      const signerRole = settings?.responsabil_functie || 'Responsabil Metrolog';
      const res = await api.post('/emm/generate-pdf', {
        signer_name: signerName,
        signer_role: signerRole,
        use_default_signer: true
      }, { responseType: 'arraybuffer' });
      setPdfData(Array.from(new Uint8Array(res.data)));
      setShowPdf(true);
      setPageNumber(1);
    } catch (err) {
      toast.error('Eroare la generarea raportului PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!pdfData) return;
    const blob = new Blob([new Uint8Array(pdfData)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Lista_EMM_${new Date().toISOString().slice(0,10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = items.filter(item => {
    if (!item.is_active) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (item.denumire || '').toLowerCase().includes(s) ||
           (item.tip_serie || '').toLowerCase().includes(s) ||
           (item.domeniu || '').toLowerCase().includes(s) ||
           (item.localizare || '').toLowerCase().includes(s);
  });

  const statusCounts = filtered.reduce((acc, item) => {
    const st = item.verification_status || 'necunoscut';
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  // PDF View
  if (showPdf) {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8" data-testid="emm-pdf-view">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => setShowPdf(false)} data-testid="back-to-list-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Inapoi la lista
          </Button>
          <Button onClick={handleDownloadPdf} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="download-emm-pdf">
            <Download className="w-4 h-4 mr-2" /> Descarca PDF
          </Button>
        </div>
        <div ref={containerRefCallback}>
          <div className="flex items-center justify-between bg-slate-800 text-white rounded-t-lg px-3 py-2">
            <div className="flex items-center gap-1">
              <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}
                className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30" data-testid="emm-pdf-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm px-2 min-w-[80px] text-center">{pageNumber} / {numPages || '...'}</span>
              <button onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))} disabled={pageNumber >= (numPages || 1)}
                className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30" data-testid="emm-pdf-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setScale(s => Math.max(0.5, +(s - 0.1).toFixed(1)))}
                className="p-1.5 rounded hover:bg-slate-700" data-testid="emm-pdf-zoom-out">
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs px-2 min-w-[48px] text-center">{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale(s => Math.min(2.5, +(s + 0.1).toFixed(1)))}
                className="p-1.5 rounded hover:bg-slate-700" data-testid="emm-pdf-zoom-in">
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="border border-t-0 border-slate-200 rounded-b-lg bg-slate-100 overflow-auto" style={{ maxHeight: '75vh' }}>
            <div className="flex justify-center py-2">
              {containerWidth > 0 && (
                <Document file={fileProp}
                  onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  onLoadError={(err) => console.error('PDF error:', err)}
                  loading={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}>
                  <Page pageNumber={pageNumber} width={containerWidth * scale} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main List View
  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8" data-testid="audit-emm-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="emm-title">Nomenclator EMM</h1>
            <p className="text-sm text-slate-500 mt-1">Echipamente de Masurare si Monitorizare</p>
          </div>
          <Button
            onClick={handleGeneratePdf}
            disabled={pdfLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-11"
            data-testid="generate-emm-pdf-btn"
          >
            {pdfLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Se genereaza...</>
            ) : (
              <><FileText className="w-4 h-4 mr-2" /> Genereaza Lista PDF</>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Gauge className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{filtered.length}</p>
              <p className="text-xs text-slate-500">Total EMM</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{statusCounts.valid || 0}</p>
              <p className="text-xs text-slate-500">Valide</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{(statusCounts.expirat || 0) + (statusCounts.expira_curand || 0)}</p>
              <p className="text-xs text-slate-500">Necesita atentie</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cauta echipament EMM..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10 h-11"
          data-testid="emm-search"
        />
      </div>

      {/* EMM Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Gauge className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Niciun echipament EMM gasit</p>
        </div>
      ) : (
        <Card className="border-0 shadow-sm overflow-hidden" data-testid="emm-table-card">
          {/* Desktop Table */}
          <div className="hidden lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left font-semibold text-slate-600 px-4 py-3 w-[40px]">Nr.</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Denumire echipament</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Tip / Serie</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Domeniu</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Localizare</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Verificat pana la</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Interval</th>
                  <th className="text-center font-semibold text-slate-600 px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => {
                  const st = STATUS_CONFIG[item.verification_status] || STATUS_CONFIG.necunoscut;
                  return (
                    <tr key={item.id || idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`emm-row-${idx}`}>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">{idx + 1}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{item.denumire}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{item.tip_serie || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{item.domeniu || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{item.localizare || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{item.verificat_pana_la || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{item.interval_verificare || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${st.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y divide-slate-100">
            {filtered.map((item, idx) => {
              const st = STATUS_CONFIG[item.verification_status] || STATUS_CONFIG.necunoscut;
              return (
                <div key={item.id || idx} className="p-4" data-testid={`emm-card-${idx}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-slate-400 font-mono">{idx + 1}.</span>
                      <h3 className="font-semibold text-slate-900 text-sm truncate">{item.denumire}</h3>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${st.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                      {st.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
                    {item.tip_serie && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400">Serie:</span> {item.tip_serie}
                      </div>
                    )}
                    {item.domeniu && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400">Domeniu:</span> {item.domeniu}
                      </div>
                    )}
                    {item.localizare && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-slate-400" /> {item.localizare}
                      </div>
                    )}
                    {item.verificat_pana_la && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" /> {item.verificat_pana_la}
                      </div>
                    )}
                    {item.interval_verificare && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" /> {item.interval_verificare}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AuditEMMPage;
